// Radha — company-name lens.
//
// The demo book is Indian; a Germany lens showing "Dabur India Limited" in
// every table would give the game away. So when the country lens points away
// from home, DOMESTIC companies render under country-appropriate FICTIONAL
// names (sector-matched: a bank maps to a bank, a cement maker to a cement
// maker), while genuine multinationals keep their global brand everywhere —
// Unilever is Unilever in Frankfurt and in Riyadh.
//
// Strictly display: the mapping is applied to rendered text via a DOM walker
// (see attachNameLens) and never touches queries, writes or exports. The
// assignment is deterministic — the same Indian company is always the same
// fictional company within a country — so drill-downs stay coherent.
// Every fictional name was invented for this file; collisions with real
// firms are accidental.

// Multinationals: shown under the parent's global brand in every non-home lens.
const MNC_GLOBAL: Record<string, string> = {
  "ABB India Limited": "ABB",
  "Bosch Limited": "Bosch",
  "Siemens Limited": "Siemens",
  "Colgate-Palmolive (India) Limited": "Colgate-Palmolive",
  "Cummins India Limited": "Cummins Inc.",
  "Hindustan Unilever Limited": "Unilever",
  "Whirlpool of India Limited": "Whirlpool Corporation",
  "Maruti Suzuki India Limited": "Suzuki Motor Corp.",
  // Indian-headquartered multinationals — global names in their own right.
  "Tata Consultancy Services": "Tata Consultancy Services",
  "Infosys Ltd": "Infosys Ltd",
  "Wipro Ltd": "Wipro Ltd",
  "Tata Motors Limited": "Tata Motors",
  "Mahindra & Mahindra": "Mahindra & Mahindra",
  "Larsen & Toubro Limited": "Larsen & Toubro",
  "L&T Finance Holdings": "L&T Finance",
};

// Domestic companies, sector-tagged so replacements stay plausible.
type Sector = "fin" | "auto" | "fmcg" | "mat" | "chem" | "eng" | "tel" | "energy" | "retail";
const DOMESTIC: Record<string, Sector> = {
  "HDFC Bank Ltd": "fin", "ICICI Bank Limited": "fin", "Axis Bank Limited": "fin",
  "State Bank of India": "fin", "Bajaj Finance Ltd": "fin",
  "Ashok Leyland Limited": "auto", "Bajaj Auto Limited": "auto",
  "Eicher Motors Limited": "auto", "Hero MotoCorp Limited": "auto",
  "ITC Limited": "fmcg", "Britannia Industries Limited": "fmcg", "Dabur India Limited": "fmcg",
  "Godrej Consumer Products Limited": "fmcg", "Marico Limited": "fmcg",
  "Titan Company Limited": "fmcg", "Page Industries Limited": "fmcg",
  "UltraTech Cement Limited": "mat", "Grasim Industries Limited": "mat",
  "JSW Steel Limited": "mat", "Century Plyboards Limited": "mat", "Bharat Forge Limited": "mat",
  "Asian Paints Limited": "chem", "Berger Paints India Limited": "chem", "Pidilite Industries Limited": "chem",
  "Havells India Limited": "eng", "Voltas Limited": "eng", "Blue Star Limited": "eng",
  "Dixon Technologies Limited": "eng", "Ganga Engineering Works": "eng", "Shakti Precision Components": "eng",
  "Bharti Airtel Limited": "tel",
  "Reliance Industries Ltd": "energy", "Adani Enterprises Limited": "energy",
  "Trent Limited": "retail",
};

// Per-country fictional pools, sized to the sector counts above
// (fin 5, auto 4, fmcg 7, mat 5, chem 3, eng 6, tel 1, energy 2, retail 1).
const POOLS: Record<string, Record<Sector, string[]>> = {
  US: {
    fin: ["Sterling National Bank", "Commonwealth Trust Co.", "Blue Harbor Bancorp", "First Meridian Bank", "Crestpoint Capital"],
    auto: ["Liberty Motor Works", "Redline Truck Corp.", "Falcon Powersports", "Great Lakes Motors"],
    fmcg: ["Pinnacle Consumer Brands", "Harvest Ridge Foods", "Clearwater Naturals", "Beacon Home Products", "Silverleaf Goods", "Summit Apparel Group", "Goldcrest Timepieces"],
    mat: ["Crestview Cement Co.", "Ironbound Materials", "Appalachian Steelworks", "Timberline Panels", "Forge & Anvil Industries"],
    chem: ["Coastal Coatings Inc.", "Heritage Paint Works", "Bondright Adhesives"],
    eng: ["Lakeshore Electricals", "Polar Air Systems", "Summit Cooling Corp.", "Vector Assembly Inc.", "Riverside Engineering", "Precision Path Components"],
    tel: ["Frontier Communications Group"],
    energy: ["Gulfport Energy Holdings", "Keystone Ventures Corp."],
    retail: ["Lakewood Retail Group"],
  },
  GB: {
    fin: ["Northbridge Bank plc", "Albion Trust", "Caledonia Capital", "Sterling Rowe & Co.", "Kingsway Finance"],
    auto: ["Regency Motors Ltd", "Blackfriars Commercial Vehicles", "Trident Motorcycles", "Pennine Automotive"],
    fmcg: ["Fenwick & Sons Foods", "Harrow Biscuit Company", "Wellbourne Naturals", "Hartley Home Goods", "Clarendon Brands", "Savile Apparel", "Whitmore Jewellers"],
    mat: ["Portland Rock Cement", "Mersey Materials", "Sheffield Steel Partners", "Chiltern Boards", "Anvil Forgeworks"],
    chem: ["Thameside Coatings", "Bristol Paint Co.", "Bondfast Adhesives"],
    eng: ["Clyde Electricals", "Westray Air Systems", "Camden Cooling", "Ashworth Assembly", "Tyne Engineering", "Marlow Precision"],
    tel: ["Meridian Telecom plc"],
    energy: ["North Sea Industries", "Grosvenor Ventures"],
    retail: ["Kensington Retail Group"],
  },
  DE: {
    fin: ["Süddeutsche Handelsbank", "Rheinland Bank AG", "Hansa Kredit", "Bayerische Volksfinanz", "Falkenberg Kapital"],
    auto: ["Neckar Nutzfahrzeuge", "Schwarzwald Motoren", "Adler Zweirad AG", "Elbtal Automobil"],
    fmcg: ["Lindenhof Konsumgüter", "Bergmann Backwaren", "Naturkraft GmbH", "Hauswerk Produkte", "Weissland Marken", "Tuchwerk Bekleidung", "Goldmann Uhren"],
    mat: ["Neckar Zement AG", "Ruhrtal Baustoffe", "Weserstahl AG", "Tannenholz Platten", "Schmiedewerk Hagen"],
    chem: ["Rheinfarben GmbH", "Berliner Lackwerke", "Klebstoff Fischer"],
    eng: ["Nordstern Elektrik", "Alpenluft Klima", "Kühltechnik Weber", "Montagewerk Sachs", "Isartal Ingenieure", "Präzision Krüger"],
    tel: ["Telekom Taunus"],
    energy: ["Nordwind Energie AG", "Hanse Beteiligungen"],
    retail: ["Kaufhaus Lindner"],
  },
  AE: {
    fin: ["Gulf Crescent Bank", "Al Noor Finance House", "Emirates Falcon Bank", "Pearl Coast Capital", "Al Reem Credit"],
    auto: ["Al Fahd Motors", "Desert Line Trucks", "Oasis Motorcycles", "Al Wasl Automotive"],
    fmcg: ["Al Manar Consumer Goods", "Palm Grove Foods", "Zahra Naturals", "Bayt Al Khair Products", "Al Dana Brands", "Sadu Apparel", "Al Zumurrud Jewellery"],
    mat: ["Al Khaleej Cement", "Falcon Gate Materials", "Emirates Steel Traders", "Ghaf Wood Panels", "Al Sakr Forging"],
    chem: ["Coral Coatings LLC", "Al Bahr Paints", "Mubarak Adhesives"],
    eng: ["Al Diyar Electricals", "Sahara Air Systems", "Ice Crown Cooling", "Al Buraq Assembly", "Creek Engineering", "Mizan Precision"],
    tel: ["Etihad Telecom Partners"],
    energy: ["Arabian Gulf Holdings", "Al Sahm Ventures"],
    retail: ["Souq Al Madina Retail"],
  },
  SA: {
    fin: ["Najd Commercial Bank", "Al Salam Finance", "Red Sea Capital", "Tabuk Investment House", "Al Diwan Credit"],
    auto: ["Riyadh Heavy Motors", "Al Qassim Trucks", "Hejaz Motorcycles", "Dammam Automotive"],
    fmcg: ["Al Waha Consumer Goods", "Date Palm Foods", "Zamzam Naturals", "Bait Al Deera Products", "Al Yamama Brands", "Thobe & Co. Apparel", "Al Masla Jewellery"],
    mat: ["Red Sands Cement", "Arabian Gate Materials", "Jubail Steel Partners", "Acacia Panels", "Al Hadid Forging"],
    chem: ["Corniche Coatings", "Al Lawn Paints", "Sahel Adhesives"],
    eng: ["Al Faisal Electricals", "Nafud Air Systems", "Polar Palm Cooling", "Makkah Road Assembly", "Wadi Engineering", "Mithaq Precision"],
    tel: ["Salam Telecom Group"],
    energy: ["Desert Rose Industries", "Al Rimal Ventures"],
    retail: ["Souq Okaz Retail"],
  },
  FR: {
    fin: ["Banque du Rhône", "Crédit Beaumont", "Fiducie Lumière", "Banque de l'Estuaire", "Capital Ardent"],
    auto: ["Ateliers Marchand", "Poids Lourds de Loire", "Motocycles Verrier", "Automobiles Chastel"],
    fmcg: ["Maison Delacour", "Biscuiterie Aubert", "Naturalia Provence", "Produits du Foyer", "Marques Belleville", "Confection Reverdy", "Horlogerie Marceau"],
    mat: ["Ciments de Provence", "Matériaux Garonne", "Aciéries du Nord", "Panneaux Vosges", "Forge Aveline"],
    chem: ["Peintures Riviera", "Laques de Seine", "Adhésifs Colbert"],
    eng: ["Électricité Fabre", "Climatisation Mistral", "Froid Savoie", "Assemblage Perrin", "Ingénierie Rhodanienne", "Précision Vallée"],
    tel: ["Télécom Horizon"],
    energy: ["Énergies de l'Atlantique", "Participations Royale"],
    retail: ["Galeries Montreuil"],
  },
  NL: {
    fin: ["Noordzee Bank", "Van der Meer Krediet", "Tulp Kapitaal", "Bank van de Delta", "Oranje Financiën"],
    auto: ["Zuidas Bedrijfswagens", "Polder Motoren", "Fiets & Motor NV", "Wagenbouw Utrecht"],
    fmcg: ["Hollandse Waren", "Bakkerij Vermeer", "Natuurlijk Goed BV", "Huismerk Producten", "Merkenhuis Leiden", "Kleding Van Dijk", "Juwelier Brouwer"],
    mat: ["Delta Cement", "Maas Materialen", "Staalwerk Rotterdam", "Platen Veluwe", "Smederij Hendriks"],
    chem: ["Verf Van Rijn", "Lakken Zaandam", "Lijmwerk Bos"],
    eng: ["Elektra Noord", "Luchtbehandeling Zeeland", "Koeltechniek De Vries", "Montage Willems", "Ingenieurs IJssel", "Precisie Jansen"],
    tel: ["Telecom Randstad"],
    energy: ["Noordwind Holding", "Deelnemingen Amstel"],
    retail: ["Warenhuis De Linde"],
  },
  ES: {
    fin: ["Banco Levante", "Crédito Almadén", "Fiducia Ibérica", "Banco del Ebro", "Capital Girona"],
    auto: ["Talleres Navarro", "Camiones del Sur", "Motocicletas Vega", "Automoción Castell"],
    fmcg: ["Casa Herrero", "Galletas Ondara", "Natural Meseta", "Productos del Hogar SA", "Marcas Alcalá", "Confección Riera", "Joyería Solana"],
    mat: ["Cementos Ibéricos", "Materiales Guadiana", "Acerías del Cantábrico", "Tableros Sierra", "Forja Mendoza"],
    chem: ["Pinturas Mediterráneo", "Lacas Sevilla", "Adhesivos Prado"],
    eng: ["Eléctrica Montiel", "Climatización Poniente", "Frío Aragón", "Montajes Ruiz", "Ingeniería Bética", "Precisión Vidal"],
    tel: ["Telecom Meseta"],
    energy: ["Energías del Atlántico", "Participadas Colón"],
    retail: ["Almacenes Rocío"],
  },
  IT: {
    fin: ["Banca Adriatica", "Credito Baresi", "Fiducia Lombarda", "Banco del Tirreno", "Capitale Verona"],
    auto: ["Officine Moretti", "Veicoli Industriali Po", "Motocicli Serra", "Automobili Cardano"],
    fmcg: ["Casa Bellandi", "Biscottificio Aurelio", "Natura Toscana", "Prodotti per la Casa SpA", "Marchi Fiorentini", "Confezioni Rovere", "Gioielleria Massimo"],
    mat: ["Cementi Toscani", "Materiali Adige", "Acciaierie Liguri", "Pannelli Dolomiti", "Forgiatura Bruni"],
    chem: ["Vernici Riviera", "Lacche Milano", "Adesivi Ferrante"],
    eng: ["Elettrica Comense", "Climatizzazione Scirocco", "Freddo Alpino", "Montaggi Greco", "Ingegneria Padana", "Precisione Villa"],
    tel: ["Telecom Tirreno"],
    energy: ["Energie del Adriatico", "Partecipazioni Estense"],
    retail: ["Magazzini Aurora"],
  },
};

/** original -> display name for a given lens country. Stable per country. */
export function buildNameMap(country: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const [orig, global] of Object.entries(MNC_GLOBAL)) {
    if (orig !== global) map.set(orig, global);
  }
  const pools = POOLS[country];
  if (!pools) return map;   // home country or unknown: MNC globalisation only
  const counters: Record<string, number> = {};
  for (const orig of Object.keys(DOMESTIC).sort()) {
    const sector = DOMESTIC[orig];
    const pool = pools[sector] ?? [];
    const i = counters[sector] ?? 0;
    if (pool[i]) { map.set(orig, pool[i]); counters[sector] = i + 1; }
  }
  return map;
}

/**
 * Rewrites known company names inside text nodes under `root`, and keeps
 * doing so as the panel re-renders. Longest names first so "Larsen & Toubro
 * Limited" wins over any shorter overlap. Returns a detach function.
 * DirectShell remounts <main> whenever the lens changes, so stale rewrites
 * never need undoing.
 */
export function attachNameLens(root: HTMLElement, country: string): () => void {
  const map = buildNameMap(country);
  if (map.size === 0) return () => undefined;
  const names = [...map.keys()].sort((a, b) => b.length - a.length);

  const rewrite = (node: Node) => {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let t: Node | null;
    while ((t = walker.nextNode())) {
      let v = t.nodeValue ?? "";
      if (v.length < 3) continue;
      let changed = false;
      for (const n of names) {
        if (v.includes(n)) { v = v.split(n).join(map.get(n)!); changed = true; }
      }
      if (changed) t.nodeValue = v;
    }
  };

  rewrite(root);
  const obs = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((n) => rewrite(n));
      if (m.type === "characterData" && m.target.parentNode) rewrite(m.target.parentNode);
    }
  });
  obs.observe(root, { childList: true, subtree: true, characterData: true });
  return () => obs.disconnect();
}
